'use client';

import { AbstractIntlMessages, NextIntlClientProvider } from 'next-intl';
import * as Sentry from '@sentry/nextjs';
import { PropsWithChildren, useMemo } from 'react';
import { DEFAULT_LOCALE } from '..';

export const I18nProvider = ({
  children,
  defaultMessages,
  locale,
  messages,
}: PropsWithChildren<{
  defaultMessages: AbstractIntlMessages;
  locale: string;
  messages: AbstractIntlMessages;
}>) => {
  const getMessageFallback = ({ key }) => {
    return defaultMessages[key] || key;
  };

  const onError = (error) => {
    // Suppress missing message warnings for non-english locales
    if (error.code === 'MISSING_MESSAGE' && locale.toLowerCase() !== DEFAULT_LOCALE.toLowerCase()) return;

    Sentry.captureException(error);
  };

  // Detect user's timezone on client side
  const timeZone = useMemo(() => {
    if (typeof window !== 'undefined') {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        return 'UTC';
      }
    }
    return 'UTC'; // Server-side fallback
  }, []);

  return (
    <NextIntlClientProvider
      messages={messages}
      locale={locale}
      timeZone={timeZone}
      onError={onError}
      getMessageFallback={getMessageFallback}
    >
      {children}
    </NextIntlClientProvider>
  );
};
